import Commons from '../../../../../Services/Common/Common';
import dayjs from 'dayjs';
import { DEFAULT_LEVELS_3X3, DEFAULT_LEVELS_5X4 } from './Regulation';

/**
 * Step00_Regulation(실시규정) 스키마
 * - fields: 폼 필드 정의 (validateRequired, 레이블)
 * - mapper.save1: SaveRisCustomU1 API payload (SaveRisCustomU1Info + MatrixList[])
 */
export const Regulation_Schema = {
  fields: {
    rsNm: { initial: '', label: '위험성평가명', required: 'Y' },
    rsGubun: { initial: '최초평가', label: '평가구분', required: 'Y' },
    rsMethod: { initial: '빈도·강도법', label: '평가방법', required: 'Y' },
    depNm: { initial: [], label: '평가부서', required: 'Y' },
    startDate: { initial: '', label: '평가기간(시작일)', required: 'Y' },
    endDate: { initial: '', label: '평가기간(종료일)', required: 'Y' },
    scale: { initial: '3x3', label: '평가척도' },
    levels: { initial: [], label: '위험성 수준' },
    depNmCsv: { initial: null, label: '평가부서(CSV)' },
  },

  mapper: {
    /**
     * 부서 조회 (GetDepLast) - 평가부서 셀렉트 옵션용
     */
    search1: {
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd || ''),
      SearchText: (_, context) => Commons.EncryptAES(''),
    },

    /**
     * 실시규정 기본정보 저장 (SaveRisCustomU1 - SaveRisCustomU1Info)
     * 문자열 필드(CustCd, RsNm, …, RS_SCALE, UserId)는 서버에서 DecryptAES → Commons.EncryptAES
     * MatrixList(SaveRisCustomU1MatrixItem)는 평문 JSON (암호화 없음)
     */
    save1: {
      // long? 역직렬화를 위해 숫자 또는 null 전송 (암호화 안 함)
      RsCd: (row) => {
        const v = row.rsCd;
        if (v == null || v === '') return null;
        const n = Number(v);
        return Number.isNaN(n) ? null : n;
      },
      CustCd: (_, context) => Commons.EncryptAES(context.search?.custCd ?? ''),
      RsNm: (row) => Commons.EncryptAES(row.rsNm ?? ''),
      RsGubun: (row) => Commons.EncryptAES(row.rsGubun ?? ''),
      RsMethod: (row) => Commons.EncryptAES(row.rsMethod ?? ''),
      RsScale: (row) =>
        Commons.EncryptAES(
          NormalizeRsScaleForForm(row.scale ?? row.rsScale ?? '')
        ),
      DepCd: (row) =>
        Commons.EncryptAES(
          row.depCd ??
            (Array.isArray(row.depNm)
              ? row.depNm.join(',')
              : (row.depNmCsv ?? ''))
        ),
      RsStartDate: (row) => Commons.EncryptAES(row.startDate ?? ''),
      RsEndDate: (row) => Commons.EncryptAES(row.endDate ?? ''),
      /** 평가자 — 로그인 사용자 기준 */
      RsMember: (_, context) =>
        Commons.EncryptAES(
          context.userInfo?.userNm ?? context.userInfo?.userid ?? ''
        ),
      UserId: (_, context) => context.userInfo?.userid || '',
      MatrixList: (row) => {
        const levels = Array.isArray(row.levels) ? row.levels : [];
        return levels.map((lv, idx) => ({
          LevelNm: lv.label ?? '',
          MinScore: lv.min != null && lv.min !== '' ? Number(lv.min) : null,
          MaxScore: lv.max != null && lv.max !== '' ? Number(lv.max) : null,
          ActionMethod: lv.action ?? '',
          SortOrdr: idx + 1,
        }));
      },
    },
  },
};

/** Regulation 폼 Select 옵션과 동일한 문자열 (DB·서버 값 정규화용) */
export const REGULATION_RS_GUBUN_OPTIONS = ['최초평가', '정기평가', '수시평가'];
export const REGULATION_RS_METHOD_OPTIONS = [
  '빈도·강도법',
  '위험성 수준 3단계판단법',
];

/**
 * GetRisCustomR1Item / 그리드 행의 평가구분 → 폼용 라벨
 */
export const NormalizeRsGubunForForm = (v) => {
  if (v == null || v === '') return '최초평가';
  const s = String(v).trim();
  if (REGULATION_RS_GUBUN_OPTIONS.includes(s)) return s;
  const c = s.replace(/\s+/g, '');
  if (c.includes('최초') || s === '1') return '최초평가';
  if (c.includes('정기') || s === '2') return '정기평가';
  if (c.includes('수시') || s === '3') return '수시평가';
  return s;
};

/**
 * GetRisCustomR1Item / 그리드 행의 평가방법 → 폼용 라벨
 * (서버: "위험성 수준 3단계판단법" 등 띄어쓰기 차이 허용)
 */
export const NormalizeRsMethodForForm = (v) => {
  if (v == null || v === '') return '빈도·강도법';
  const s = String(v).trim();
  if (REGULATION_RS_METHOD_OPTIONS.includes(s)) return s;
  const c = s.replace(/\s+/g, '');
  if (c.includes('빈도') || c.includes('강도')) return '빈도·강도법';
  if (c.includes('3단계') || c.includes('판단법') || c.includes('판단'))
    return '위험성 수준 3단계판단법';
  return s;
};

/** GetRisCustomR1Item.RsScale → 폼 라디오 값 */
export const NormalizeRsScaleForForm = (v) => {
  if (v == null || v === '') return '3x3';
  const s = String(v)
    .trim()
    .replace(/\s+/g, '')
    .replace(/×/g, 'x')
    .toLowerCase();
  if (s === '5x4') return '5x4';
  return '3x3';
};

/**
 * GetRisCustomR1MatrixItem[] → Regulation levels (label/min/max/action/tone)
 */
const MapMatrixListToRegulationLevels = (matrixList, scale) => {
  const defaults = scale === '5x4' ? DEFAULT_LEVELS_5X4 : DEFAULT_LEVELS_3X3;
  const palette =
    scale === '5x4'
      ? ['green', 'lime', 'yellow', 'peach', 'orange', 'red']
      : ['green', 'yellow', 'red'];

  const sorted = [...(matrixList || [])].sort((a, b) => {
    const sa = a.SortOrdr ?? a.sortOrdr ?? 0;
    const sb = b.SortOrdr ?? b.sortOrdr ?? 0;
    return Number(sa) - Number(sb);
  });

  return sorted.map((m, idx) => {
    const levelNm = m.LevelNm ?? m.levelNm ?? '';
    const minScore = m.MinScore ?? m.minScore;
    const maxScore = m.MaxScore ?? m.maxScore;
    const actionMethod = m.ActionMethod ?? m.actionMethod ?? '';
    const min = minScore != null ? Number(minScore) : 0;
    const max = maxScore != null ? Number(maxScore) : 0;

    const exact = defaults.find(
      (d) =>
        d.action === actionMethod &&
        Number(d.min) === min &&
        Number(d.max) === max
    );
    const byAction = defaults.find((d) => d.action === actionMethod);
    const tone = exact?.tone ?? byAction?.tone ?? palette[idx] ?? 'yellow';

    return {
      label: levelNm,
      min,
      max,
      action: actionMethod,
      tone,
    };
  });
};

/**
 * GetRisCustomR1 그리드 행 → Regulation 기본정보 폼 초기값
 * @param {object} row - rsCd, rsNm, rsGubun, rsMethod, rsScale, matrixList, depNm, 날짜 …
 * @param {Array<{ value: string, label: string }>} deptItems - GetDepLast 옵션
 */
export const MapRisCustomGridRowToRegulationForm = (row, deptItems = []) => {
  if (!row) return null;
  const depNmStr = row.depNm ?? '';
  const names = String(depNmStr)
    .split(/[,，]/)
    .map((t) => t.trim())
    .filter(Boolean);
  const depCds = [];
  for (const name of names) {
    const hit = deptItems.find(
      (d) =>
        String(d.label ?? '').trim() === name ||
        String(d.depNmLast ?? d.depNm ?? '').trim() === name
    );
    if (hit?.value) depCds.push(String(hit.value));
  }
  const rsCdRaw = row.rsCd;
  const rsCdNum = rsCdRaw != null && rsCdRaw !== '' ? Number(rsCdRaw) : null;

  const scale = NormalizeRsScaleForForm(row.rsScale);
  const matrixRaw = row.matrixList ?? row.MatrixList ?? [];
  const hasMatrix = Array.isArray(matrixRaw) && matrixRaw.length > 0;
  const levels = hasMatrix
    ? MapMatrixListToRegulationLevels(matrixRaw, scale)
    : undefined;

  return {
    rsCd: Number.isFinite(rsCdNum) ? rsCdNum : null,
    rsNm: row.rsNm ?? '',
    rsGubun: NormalizeRsGubunForForm(row.rsGubun),
    rsMethod: NormalizeRsMethodForForm(row.rsMethod),
    depNm: depCds,
    depNmCsv: depCds.length ? depCds.join(',') : null,
    startDate: row.rsStartDate || row.rsDate || dayjs().format('YYYY-MM-DD'),
    endDate:
      row.rsEndDate ||
      row.rsStartDate ||
      row.rsDate ||
      dayjs().format('YYYY-MM-DD'),
    scale,
    ...(levels ? { levels } : {}),
  };
};

export default Regulation_Schema;
